import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ChatAudioPlayer = ({ src, isUser }) => {
    const { t } = useTranslation(['chat']);
    const audioRef = useRef(null);
    const progressRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onLoadedMetadata = () => setDuration(audio.duration);
        const onDurationChange = () => {
            if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration);
        };
        const onTimeUpdate = () => setCurrentTime(audio.currentTime);
        const onEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
            audio.currentTime = 0;
        };
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);

        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('durationchange', onDurationChange);
        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('ended', onEnded);
        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);

        return () => {
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('durationchange', onDurationChange);
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
        };
    }, []);

    const togglePlayPause = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
        } else {
            audio.play();
        }
    };

    const handleProgressClick = (e) => {
        const audio = audioRef.current;
        const bar = progressRef.current;
        if (!audio || !bar || !duration) return;

        const rect = bar.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const newTime = (clickX / rect.width) * duration;
        audio.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    // Colors based on sender/receiver
    const btnBg = isUser ? 'bg-white/20 hover:bg-white/30' : 'bg-[#4ABDBD]/10 hover:bg-[#4ABDBD]/20';
    const btnIcon = isUser ? 'text-white' : 'text-[#4ABDBD]';
    const trackBg = isUser ? 'bg-white/20' : 'bg-gray-200';
    const trackFill = isUser ? 'bg-white' : 'bg-[#4ABDBD]';
    const thumbColor = isUser ? 'bg-white' : 'bg-[#4ABDBD]';
    const timeText = isUser ? 'text-white/70' : 'text-gray-500';
    const labelText = isUser ? 'text-white' : 'text-gray-700';

    return (
        <div className="min-w-[240px] p-3" data-testid="chat-audio-player-container">
            <audio ref={audioRef} src={src} preload="metadata" data-testid="chat-audio-player-element" />

            <div className="flex items-center gap-2 mb-2">
                <Volume2 size={16} className={btnIcon} data-testid="chat-audio-player-volume-icon" />
                <span className={`text-xs font-bold ${labelText}`} data-testid="chat-audio-player-label-text">{t('chat.voice_message', 'Voice Message')}</span>
            </div>


            <div className="flex items-center gap-3">
                {/* Play/Pause Button */}
                <button
                    type="button"
                    onClick={togglePlayPause}
                    className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${btnBg}`}
                    data-testid="chat-audio-player-play-pause-button"
                >
                    {isPlaying
                        ? <Pause size={16} className={btnIcon} fill="currentColor" data-testid="chat-audio-player-pause-icon" />
                        : <Play size={16} className={`${btnIcon} ml-0.5`} fill="currentColor" data-testid="chat-audio-player-play-icon" />
                    }
                </button>

                {/* Progress + Time */}
                <div className="flex-1 min-w-0" data-testid="chat-audio-player-progress-container">
                    {/* Progress Bar */}
                    <div
                        ref={progressRef}
                        onClick={handleProgressClick}
                        className={`relative w-full h-1.5 rounded-full cursor-pointer ${trackBg}`}
                        data-testid="chat-audio-player-progress-bar"
                    >
                        <div
                            className={`h-full rounded-full ${trackFill}`}
                            style={{ width: `${progress}%` }}
                            data-testid="chat-audio-player-progress-fill"
                        />
                        {/* Thumb indicator */}
                        <div
                            className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full shadow-sm ${thumbColor}`}
                            style={{ left: `calc(${progress}% - 6px)` }}
                            data-testid="chat-audio-player-progress-thumb"
                        />
                    </div>
                    {/* Time */}
                    <div className={`flex justify-between mt-1.5 text-[10px] font-mono ${timeText}`}>
                        <span data-testid="chat-audio-player-current-time-text">{formatTime(currentTime)}</span>
                        <span data-testid="chat-audio-player-duration-text">{formatTime(duration)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatAudioPlayer;
